/* eslint-disable sort-imports */
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { DatePickerInput } from 'src/components/date-picker/date-picker-input';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import React, { useEffect, useState } from 'react';
import {
    DateTermSettingsService,
    ISettingsItem,
} from 'src/services/date-term-settings.service';
import { useLocalization } from 'src/utils/useLocalization';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { MODAL_CONTROL_ACTIONS } from 'src/utils/enums/user-store-actions.enum';
import { IUser } from 'src/utils/interfaces/user';
import {
    ADD_NEW_USER_MODAL_LABEL_BUTTON_CANCEL,
    ADD_NEW_USER_MODAL_LABEL_BUTTON_CONFIRM,
} from 'src/localization/resouceKeys';
import './styles.scss';

interface IDateTermForm {
    usaDate: string;
    canadaDate: string;
}

const MONTH_OFFSET = 1;
const DATE_PAD_LENGTH = 2;
const USA_SETTING_KEY = 'USTERMDATE';
const CANADA_SETTING_KEY = 'CANADATERMDATE';

const formatDateForRequest = (isoDate: string) => {
    if (!isoDate) {
        return '';
    }

    const [year, month, day] = isoDate.split('-');

    if (!year || !month || !day) {
        return '';
    }

    return `${month}/${day}/${year}`;
};

const getCurrentValueText = (baseText: string, value?: string) => {
    if (!value) {
        return baseText;
    }

    return baseText.replace('{date}', value);
};

const getTodayDateString = () => {
    const today = new Date();
    const month = `${today.getMonth() + MONTH_OFFSET}`.padStart(
        DATE_PAD_LENGTH,
        '0',
    );
    const day = `${today.getDate()}`.padStart(DATE_PAD_LENGTH, '0');

    return `${today.getFullYear()}-${month}-${day}`;
};

const DateAndTermComponent: React.FC = () => {
    const { systemAdmin, addNewUser } = useLocalization();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const brand: string = useSelector((state: IUser) => {
        return state.dealerDataInfo?.dealer.brand || 'vw';
    });
    const [settingsService] = useState(() => new DateTermSettingsService());
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showSuccessNotification, setShowSuccessNotification] =
        useState<boolean>(false);
    const [showErrorNotification, setShowErrorNotification] =
        useState<boolean>(false);
    const [initialFormValues, setInitialFormValues] = useState<IDateTermForm>({
        usaDate: '',
        canadaDate: '',
    });
    const [currentDateLabels, setCurrentDateLabels] = useState({
        usa: '',
        canada: '',
    });

    const todayDate = getTodayDateString();

    const handleActiveModal = () => {
        dispatch({
            type: MODAL_CONTROL_ACTIONS.SET_ACTIVE,
            payload: {
                titleContent: 'Are you sure you want to leave?',
                bodyContent: 'Leaving this form will discard all your changes.',
                cancelBtnLabel: t(ADD_NEW_USER_MODAL_LABEL_BUTTON_CANCEL),
                confirmBtnLabel: t(ADD_NEW_USER_MODAL_LABEL_BUTTON_CONFIRM),
                onCancelHandler: () => {
                    dispatch({
                        type: MODAL_CONTROL_ACTIONS.SET_SHOW,
                        payload: { shouldShow: false },
                    });
                },
                onConfirmHandler: () => {
                    navigate('/dashboard/systemadmin');
                },
            },
        });
    };

    useEffect(() => {
        handleActiveModal();
        return () => {
            dispatch({ type: MODAL_CONTROL_ACTIONS.SET_INACTIVE });
        };
    }, []);

    const isDateTodayOrFuture = (value?: string) => {
        if (!value) {
            return true;
        }

        return value >= todayDate;
    };

    const schema = Yup.object({
        usaDate: Yup.string()
            .required(systemAdmin.DATE_TERM_PAGE_REQUIRED_ERROR)
            .test(
                'usa-date-not-in-past',
                systemAdmin.DATE_TERM_PAGE_INVALID_DATE_ERROR,
                isDateTodayOrFuture,
            ),
        canadaDate: Yup.string()
            .required(systemAdmin.DATE_TERM_PAGE_REQUIRED_ERROR)
            .test(
                'canada-date-not-in-past',
                systemAdmin.DATE_TERM_PAGE_INVALID_DATE_ERROR,
                isDateTodayOrFuture,
            ),
    });

    const formik = useFormik<IDateTermForm>({
        initialValues: initialFormValues,
        enableReinitialize: true,
        validationSchema: schema,
        validateOnMount: true,
        onSubmit: async (values) => {
            setShowSuccessNotification(false);
            setShowErrorNotification(false);
            setIsSaving(true);

            try {
                const settingsPayload: ISettingsItem[] = [
                    {
                        keyName: USA_SETTING_KEY,
                        keyValue: formatDateForRequest(values.usaDate),
                    },
                    {
                        keyName: CANADA_SETTING_KEY,
                        keyValue: formatDateForRequest(values.canadaDate),
                    },
                ];

                await settingsService.saveSettings(settingsPayload);

                const emptyFormValues = {
                    usaDate: '',
                    canadaDate: '',
                };

                formik.resetForm({
                    values: emptyFormValues,
                });
                setInitialFormValues(emptyFormValues);
                setCurrentDateLabels({
                    usa: getCurrentValueText(
                        systemAdmin.DATE_TERM_PAGE_CURRENTLY_SET_TEXT,
                        settingsPayload[0].keyValue,
                    ),
                    canada: getCurrentValueText(
                        systemAdmin.DATE_TERM_PAGE_CURRENTLY_SET_TEXT,
                        settingsPayload[1].keyValue,
                    ),
                });
                setShowSuccessNotification(true);
            } catch {
                setShowErrorNotification(true);
            } finally {
                setIsSaving(false);
            }
        },
    });

    useEffect(() => {
        const loadSettings = async () => {
            setIsLoading(true);

            try {
                const response = await settingsService.getSettings([
                    USA_SETTING_KEY,
                    CANADA_SETTING_KEY,
                ]);
                const data = response.data as
                    | { settings?: ISettingsItem[] }
                    | ISettingsItem[];
                const settings = Array.isArray(data)
                    ? data
                    : data.settings || [];

                const usaSetting = settings.find(
                    (item) => item.keyName === USA_SETTING_KEY,
                );
                const canadaSetting = settings.find(
                    (item) => item.keyName === CANADA_SETTING_KEY,
                );

                setInitialFormValues({
                    usaDate: '',
                    canadaDate: '',
                });
                setCurrentDateLabels({
                    usa: getCurrentValueText(
                        systemAdmin.DATE_TERM_PAGE_CURRENTLY_SET_TEXT,
                        usaSetting?.keyValue,
                    ),
                    canada: getCurrentValueText(
                        systemAdmin.DATE_TERM_PAGE_CURRENTLY_SET_TEXT,
                        canadaSetting?.keyValue,
                    ),
                });
            } catch {
                setShowErrorNotification(true);
            } finally {
                setIsLoading(false);
            }
        };

        loadSettings();
    }, [
        settingsService,
        systemAdmin.DATE_TERM_PAGE_CURRENTLY_SET_TEXT,
        systemAdmin.DATE_TERM_PAGE_SYSTEM_ERROR_MESSAGE,
    ]);

    const hasError = (field: keyof IDateTermForm) =>
        !!(formik.touched[field] && formik.errors[field]);

    return (
        <>
            <div
                className={`date-term-change__nav-header ${brand.toLowerCase()}`}
            >
                <div className='one'>
                    <button
                        className='c-btn c-btn--link c-btn--small c-btn--simple'
                        type='button'
                        onClick={() => {
                            dispatch({
                                type: MODAL_CONTROL_ACTIONS.SET_SHOW,
                                payload: { shouldShow: true },
                            });
                        }}
                    >
                        <i
                            className='c-btn__icon c-icon c-icon--[semantic-back]'
                            aria-hidden='true'
                            role='img'
                        ></i>
                        <span className='c-btn__text'>
                            {addNewUser.HEADER_BUTTON_LABEL}
                        </span>
                    </button>
                </div>
            </div>
            <div className='dashboard-content-wrapper'>
                <div className='dashboard-content-container'>
                    <div className='date-term-change-wrapper'>
                        <section className='o-content-section'>
                            {showErrorNotification && (
                                <Notification
                                    content={
                                        systemAdmin.DATE_TERM_PAGE_SYSTEM_ERROR_MESSAGE
                                    }
                                    type={NotificationType.ERROR}
                                    visibility={showErrorNotification}
                                    setVisibility={setShowErrorNotification}
                                />
                            )}

                            {showSuccessNotification && (
                                <Notification
                                    content={
                                        systemAdmin.DATE_TERM_PAGE_SUCCESS_MESSAGE
                                    }
                                    type={NotificationType.SUCCESS}
                                    visibility={showSuccessNotification}
                                    setVisibility={setShowSuccessNotification}
                                />
                            )}

                            <h5>
                                <span className='c-section-heading__title-text'>
                                    {systemAdmin.DATE_TERM_PAGE_TITLE}
                                </span>
                            </h5>

                            <section className='c-form-section'>
                                <div className='c-form-section__content'>
                                    <p className='date-term-change-wrapper__description'>
                                        {systemAdmin.DATE_TERM_PAGE_DESCRIPTION}
                                    </p>

                                    <div className='o-layout date-term-change-wrapper__country-grid'>
                                        <div className='o-layout__item u-1/2 u-1/1@xs'>
                                            <p className='date-term-change-wrapper__country-title'>
                                                {
                                                    systemAdmin.DATE_TERM_PAGE_COUNTRY_USA
                                                }
                                            </p>

                                            {isLoading ? (
                                                <div className='date-term-change-wrapper__skeleton' />
                                            ) : currentDateLabels.usa ? (
                                                <p className='date-term-change-wrapper__current-value'>
                                                    {currentDateLabels.usa}
                                                </p>
                                            ) : null}

                                            <div className='c-form-field u-mb-large date-term-change-wrapper__date-field'>
                                                <label
                                                    className='c-form-field__label'
                                                    htmlFor='date-term-change-usa-date'
                                                >
                                                    {
                                                        systemAdmin.DATE_TERM_PAGE_DATE_LABEL
                                                    }
                                                </label>
                                                <div id='date-term-change-usa-date'>
                                                    <DatePickerInput
                                                        selectedDate={
                                                            formik.values
                                                                .usaDate
                                                        }
                                                        disabled={
                                                            isSaving ||
                                                            isLoading
                                                        }
                                                        handleOnClose={(
                                                            dateValue,
                                                        ) => {
                                                            setShowSuccessNotification(
                                                                false,
                                                            );
                                                            setShowErrorNotification(
                                                                false,
                                                            );
                                                            formik.setFieldValue(
                                                                'usaDate',
                                                                dateValue,
                                                            );
                                                        }}
                                                    />
                                                </div>
                                                {hasError('usaDate') ? (
                                                    <span className='c-error-message'>
                                                        {formik.errors.usaDate}
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>

                                        <div className='o-layout__item u-1/2 u-1/1@xs'>
                                            <p className='date-term-change-wrapper__country-title'>
                                                {
                                                    systemAdmin.DATE_TERM_PAGE_COUNTRY_CANADA
                                                }
                                            </p>

                                            {isLoading ? (
                                                <div className='date-term-change-wrapper__skeleton' />
                                            ) : currentDateLabels.canada ? (
                                                <p className='date-term-change-wrapper__current-value'>
                                                    {currentDateLabels.canada}
                                                </p>
                                            ) : null}

                                            <div className='c-form-field u-mb-large date-term-change-wrapper__date-field'>
                                                <label
                                                    className='c-form-field__label'
                                                    htmlFor='date-term-change-canada-date'
                                                >
                                                    {
                                                        systemAdmin.DATE_TERM_PAGE_DATE_LABEL
                                                    }
                                                </label>
                                                <div id='date-term-change-canada-date'>
                                                    <DatePickerInput
                                                        selectedDate={
                                                            formik.values
                                                                .canadaDate
                                                        }
                                                        disabled={
                                                            isSaving ||
                                                            isLoading
                                                        }
                                                        handleOnClose={(
                                                            dateValue,
                                                        ) => {
                                                            setShowSuccessNotification(
                                                                false,
                                                            );
                                                            setShowErrorNotification(
                                                                false,
                                                            );
                                                            formik.setFieldValue(
                                                                'canadaDate',
                                                                dateValue,
                                                            );
                                                        }}
                                                    />
                                                </div>
                                                {hasError('canadaDate') ? (
                                                    <span className='c-error-message'>
                                                        {
                                                            formik.errors
                                                                .canadaDate
                                                        }
                                                    </span>
                                                ) : null}
                                            </div>
                                        </div>
                                    </div>

                                    <div className='o-fieldset__row button-group date-term-change-wrapper__button-group'>
                                        <button
                                            className='c-btn c-btn--secondary'
                                            type='button'
                                            onClick={() => {
                                                setShowSuccessNotification(
                                                    false,
                                                );
                                                setShowErrorNotification(false);
                                                formik.resetForm({
                                                    values: initialFormValues,
                                                });
                                            }}
                                            disabled={isSaving || isLoading}
                                        >
                                            <span className='c-btn__text'>
                                                {
                                                    systemAdmin.DATE_TERM_PAGE_CANCEL_BUTTON
                                                }
                                            </span>
                                        </button>

                                        <button
                                            className='c-btn c-btn--primary'
                                            type='button'
                                            onClick={() => {
                                                formik.submitForm();
                                            }}
                                            disabled={
                                                !formik.values.usaDate ||
                                                !formik.values.canadaDate ||
                                                !formik.isValid ||
                                                isSaving ||
                                                isLoading
                                            }
                                        >
                                            <span className='c-btn__text'>
                                                {
                                                    systemAdmin.DATE_TERM_PAGE_SUBMIT_BUTTON
                                                }
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </section>
                        </section>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DateAndTermComponent;
